import * as React from "react";
import { twMerge } from "tailwind-merge";
import { SVGs } from "../assets";
import Text from "./Text";

export type CollapseItem = {
  key: string | number;
  label: React.ReactNode;
  extra?: React.ReactNode;
  children: React.ReactNode;
};

export type CollapseProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "title" | "onChange"
> & {
  as?: React.ElementType;
  title?: string;
  items: CollapseItem[];
  accordion?: boolean;
  activeKey?: Array<string | number> | string | number;
  defaultActiveKey?: Array<string | number> | string | number;
  onChange?: (key: string[] | string | number) => void;
  className?: string;
};

function normalizeKeys(
  key?: Array<string | number> | string | number,
): string[] {
  if (key == null) return [];
  if (Array.isArray(key)) return key.map(String);
  return [String(key)];
}

function renderPanelLabel(label: React.ReactNode, extra?: React.ReactNode) {
  if (typeof label === "string" || typeof label === "number") {
    return (
      <div className="flex flex-1 flex-wrap items-center gap-[12px]">
        <Text level={3}>{label}</Text>
        {extra}
      </div>
    );
  }

  if (extra) {
    return (
      <div className="flex flex-wrap items-center gap-[12px]">
        {label}
        {extra}
      </div>
    );
  }

  return label;
}

type CollapsePanelProps = {
  panelKey: string | number;
  label: React.ReactNode;
  extra?: React.ReactNode;
  expanded: boolean;
  onToggle: (key: string | number) => void;
  children: React.ReactNode;
};

function CollapsePanel({
  panelKey,
  label,
  extra,
  expanded,
  onToggle,
  children,
}: CollapsePanelProps) {
  const bodyId = `collapse-panel-${panelKey}`;
  const headerId = `collapse-header-${panelKey}`;

  return (
    <div className="border-b border-divider last:border-b-0">
      <button
        type="button"
        id={headerId}
        aria-expanded={expanded}
        aria-controls={bodyId}
        onClick={() => onToggle(panelKey)}
        className="flex w-full items-center justify-between gap-[12px] bg-surface py-[16px] text-left"
      >
        {renderPanelLabel(label, extra)}
        <SVGs.ChevronDown
          className={twMerge(
            "h-[11px] w-[11px] transition-transform duration-200",
            expanded && "rotate-180",
          )}
        />
      </button>
      {expanded && (
        <div
          id={bodyId}
          role="region"
          aria-labelledby={headerId}
          className="bg-surface pb-[16px]"
        >
          {children}
        </div>
      )}
    </div>
  );
}

export default function Collapse({
  as,
  className,
  title,
  items,
  accordion,
  activeKey,
  defaultActiveKey,
  onChange,
  ...rest
}: CollapseProps) {
  const Comp = (as ?? "section") as React.ElementType;
  const [internalActiveKey, setInternalActiveKey] = React.useState(() =>
    normalizeKeys(defaultActiveKey),
  );
  const activeKeys =
    activeKey !== undefined ? normalizeKeys(activeKey) : internalActiveKey;

  const onToggle = React.useCallback(
    (key: string | number) => {
      const keyStr = String(key);
      const isActive = activeKeys.includes(keyStr);
      const next = isActive
        ? activeKeys.filter((item) => item !== keyStr)
        : accordion
          ? [keyStr]
          : [...activeKeys, keyStr];

      if (activeKey === undefined) setInternalActiveKey(next);
      onChange?.(next);
    },
    [accordion, activeKey, activeKeys, onChange],
  );

  return (
    <div {...rest}>
      <Comp
        className={twMerge(
          "rounded-[8px] overflow-hidden border border-divider bg-surface",
          className,
        )}
      >
        {title && (
          <div className="px-[20px] py-[20px] bg-table-header border-b border-divider">
            <Text level={1} className="font-[600]">
              {title}
            </Text>
          </div>
        )}
        <div className="px-[16px]">
          {items.map((item) => (
            <CollapsePanel
              key={item.key}
              panelKey={item.key}
              label={item.label}
              extra={item.extra}
              expanded={activeKeys.includes(String(item.key))}
              onToggle={onToggle}
            >
              {item.children}
            </CollapsePanel>
          ))}
        </div>
      </Comp>
    </div>
  );
}
